import { useId, useState, type FormEvent } from "react";
import { ApiError } from "../api/client";
import {
  useReimputeActivities,
  useReimputeActivity,
} from "../hooks/useActivities";
import { useProjects, useProjectWorkItems } from "../hooks/usePm";
import { buildContextRefs } from "../lib/activityLinks";
import { plural } from "../lib/format";
import { useUiStore } from "../state/uiStore";
import { Sheet } from "./Sheet";

export const DEFAULT_REIMPUTE_REASON = "imputation après coup";

const LABEL_MAX = 200;

/** Une activité à réimputer : `eventId` est son activity_start. */
export interface ReimputeTarget {
  eventId: string;
  label: string;
  projectId?: string;
}

/** Message affichable d'un refus de réimputation. */
export function reimputeErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 409 && e.code === "timesheet_not_draft")
      return "Feuille déjà soumise : rouvre-la pour réimputer";
    if (e.status === 409 && e.code === "idempotency_key_reused")
      return "Cette imputation a déjà été enregistrée avec d'autres valeurs : ferme puis rouvre la fenêtre.";
    if (e.status === 403)
      return "Réimputation non autorisée pour cette activité.";
    if (e.status === 404)
      return "Activité introuvable, elle a peut-être été corrigée.";
    if (e.status === 422 || e.status === 400)
      return "Projet ou motif refusé par TAWA.";
  }
  return "Réimputation impossible pour le moment. Réessayez dans un instant.";
}

/** Imputation après coup d'une ou plusieurs activités à un projet PM. Une seule
 * activité passe par la route unitaire, plusieurs par la route groupée (tout ou
 * rien). La clé d'idempotence naît à l'ouverture : un nouvel essai la réutilise. */
export function ReimputeSheet({
  targets,
  onClose,
}: {
  targets: ReimputeTarget[];
  onClose: () => void;
}) {
  const single = targets.length === 1 ? targets[0] : undefined;
  // Un créneau regroupé arrive en plusieurs activités qui partagent libellé
  // et projet : il se présente comme une seule.
  const first = targets[0];
  const sameLabel = targets.every((t) => t.label === first?.label)
    ? first?.label
    : undefined;
  const sameProject = targets.every((t) => t.projectId === first?.projectId)
    ? first?.projectId
    : undefined;
  const [correlationId] = useState(() => crypto.randomUUID());
  const [projectId, setProjectId] = useState(sameProject ?? "");
  const [workItemId, setWorkItemId] = useState("");
  const [reason, setReason] = useState(DEFAULT_REIMPUTE_REASON);
  const [error, setError] = useState<string | null>(null);
  const formId = useId();
  const projectFieldId = useId();
  const workItemFieldId = useId();
  const reasonFieldId = useId();

  const projects = useProjects();
  const workItems = useProjectWorkItems(projectId || undefined);
  const one = useReimputeActivity();
  const many = useReimputeActivities();
  const pushToast = useUiStore((s) => s.pushToast);

  const projectList = projects.data?.items ?? [];
  const itemList = workItems.data?.items ?? [];
  const pending = one.isPending || many.isPending;
  const canSubmit =
    !pending && !!projectId && reason.trim().length > 0 && targets.length > 0;

  const title = sameProject
    ? "Changer de projet"
    : sameLabel
      ? "Imputer à un projet"
      : `Imputer ${targets.length} activités`;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const project = projectList.find((p) => p.id === projectId);
    const workItem = itemList.find((w) => w.id === workItemId);
    const label = buildContextRefs({
      project: {
        projectId,
        projectName: project?.name,
        workItemId: workItem?.id,
        workItemTitle: workItem?.title,
      },
      lteTasks: [],
      tmTasks: [],
    }).project_label;
    const ref = {
      project_id: projectId,
      ...(workItem ? { work_item_id: workItem.id } : {}),
      ...(label ? { project_label: label.slice(0, LABEL_MAX) } : {}),
    };
    const envelope = { reason: reason.trim(), correlation_id: correlationId };
    setError(null);
    try {
      if (single) {
        await one.mutateAsync({
          startEventId: single.eventId,
          ...ref,
          ...envelope,
        });
      } else {
        await many.mutateAsync({
          ...envelope,
          items: targets.map((t) => ({ event_id: t.eventId, ...ref })),
        });
      }
      pushToast({
        variant: "info",
        title: single
          ? "Activité imputée"
          : `${targets.length} activité${plural(targets.length)} imputée${plural(targets.length)}`,
        subtitle: label,
      });
      onClose();
    } catch (err) {
      setError(reimputeErrorMessage(err));
    }
  }

  return (
    <Sheet
      title={title}
      onClose={onClose}
      footer={
        <div className="sheet-cta">
          <button
            type="submit"
            form={formId}
            className="primary"
            disabled={!canSubmit}
          >
            {pending ? "Imputation…" : "Imputer"}
          </button>
        </div>
      }
    >
      <p className="reimpute-target">
        {sameLabel ?? `${targets.length} activités sans projet`}
      </p>
      <form
        id={formId}
        className="inline-form reimpute-form"
        onSubmit={(e) => void handleSubmit(e)}
      >
        <label htmlFor={projectFieldId}>
          Projet
          <select
            id={projectFieldId}
            value={projectId}
            onChange={(e) => {
              setProjectId(e.target.value);
              setWorkItemId("");
            }}
            required
          >
            <option value="">
              {projects.isLoading
                ? "Chargement des projets…"
                : projects.isError
                  ? "Projets indisponibles"
                  : "Choisir un projet"}
            </option>
            {projectList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label htmlFor={workItemFieldId}>
          Élément de travail (facultatif)
          <select
            id={workItemFieldId}
            value={workItemId}
            onChange={(e) => setWorkItemId(e.target.value)}
            disabled={!projectId}
          >
            <option value="">Projet entier</option>
            {itemList.map((w) => (
              <option key={w.id} value={w.id}>
                {w.title}
              </option>
            ))}
          </select>
        </label>
        <label htmlFor={reasonFieldId}>
          Motif
          <textarea
            id={reasonFieldId}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={2}
            required
          />
        </label>
        {error && (
          <p className="reimpute-error" role="alert">
            {error}
          </p>
        )}
      </form>
    </Sheet>
  );
}

export default ReimputeSheet;
